import { useEffect, useState } from "react";
import { Users, Activity, TrendingUp, Loader2, Save, Trophy, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const FACTOR_LABELS = {
  formation: "Formation", compliance: "Compliance", credit: "Business Credit", funding: "Funding",
  profile: "Profile", education: "Education", documents: "Documents", contracts: "Contracts",
  security: "Security", banking: "Banking", activity: "Recent Activity",
};

export default function AdminProgress() {
  const [an, setAn] = useState(null);
  const [weights, setWeights] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/progress-analytics").then(({ data }) => setAn(data)).catch(() => {});
    api.get("/admin/progress-config").then(({ data }) => setWeights(data.weights)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/progress-config", { weights }); toast.success("Health-score weights updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  if (!an || !weights) return <div className="flex justify-center py-16 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const total = Object.values(weights).reduce((a, b) => a + Number(b || 0), 0) || 1;

  return (
    <div className="space-y-6" data-testid="admin-progress">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Users, label: "Total users", val: an.users.total },
          { icon: Activity, label: "Active (14d)", val: an.users.active_14d },
          { icon: TrendingUp, label: "Engagement", val: `${an.users.engagement_rate}%` },
          { icon: Trophy, label: "Goals completed", val: an.engagement.goals_completed },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-white/8 bg-[#121214] p-4">
            <s.icon className="w-4 h-4 text-[#D4AF37] mb-2" />
            <div className="font-heading text-2xl text-white">{s.val}</div>
            <div className="text-[11px] text-[#71717A]">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="milestone-completion">
          <h3 className="font-heading text-lg text-white mb-3">Milestone completion (platform-wide)</h3>
          <div className="space-y-2.5">
            {an.milestone_completion.map((m) => (
              <div key={m.label}>
                <div className="flex justify-between text-xs mb-1"><span className="text-[#D4D4D8]">{m.label}</span><span className="text-[#D4AF37]">{m.pct}% · {m.users}</span></div>
                <Progress value={m.pct} className="h-1.5 bg-white/10" />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
            <h3 className="font-heading text-lg text-white mb-3">Platform engagement</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {Object.entries(an.engagement).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/5 pb-1.5"><span className="text-[#A1A1AA] capitalize">{k.replace(/_/g, " ")}</span><span className="text-white">{v}</span></div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/5 p-4">
              <div className="flex items-center gap-1.5 text-emerald-300 text-xs mb-2"><Trophy className="w-4 h-4" /> Most completed</div>
              {an.most_completed.map((w) => <div key={w.label} className="text-[11px] text-[#D4D4D8] flex justify-between"><span className="truncate">{w.label}</span><span>{w.pct}%</span></div>)}
            </div>
            <div className="rounded-xl border border-amber-400/20 bg-amber-500/5 p-4">
              <div className="flex items-center gap-1.5 text-amber-300 text-xs mb-2"><AlertTriangle className="w-4 h-4" /> Most abandoned</div>
              {an.most_abandoned.map((w) => <div key={w.label} className="text-[11px] text-[#D4D4D8] flex justify-between"><span className="truncate">{w.label}</span><span>{w.pct}%</span></div>)}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="weights-config">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-lg text-white">Business Health Score weights</h3>
          <Button onClick={save} disabled={saving} data-testid="save-weights-btn" size="sm" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
          </Button>
        </div>
        <p className="text-xs text-[#71717A] mb-4">Adjust how much each factor contributes to every user's health score. Total weight: {total}.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.keys(weights).map((k) => (
            <div key={k}>
              <label className="text-xs text-[#A1A1AA] mb-1 block">{FACTOR_LABELS[k] || k} <span className="text-[#52525B]">({Math.round((weights[k] / total) * 100)}%)</span></label>
              <input type="number" min="0" value={weights[k]} onChange={(e) => setWeights((w) => ({ ...w, [k]: e.target.value === "" ? 0 : Number(e.target.value) }))}
                data-testid={`weight-${k}`} className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-9 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
